/** SPEC §19: Beheer › Instellingen › Eigen velden. */
import { useState } from 'react';
import { VELD_ENTITEIT_LABEL, VELD_ENTITEITEN, VELD_SOORT_LABEL, VELD_SOORTEN, type EigenVeld, type VeldEntiteit, type VeldSoort } from '@ep/core';
import { api } from '../lib/api';
import { useActie, useData } from '../lib/data';
import { Icoon } from '../ui/Icoon';
import { Laden, Modal, Veld, VerwijderKnop } from '../ui/ui';

type Form = { id?: string; entiteit: VeldEntiteit; naam: string; soort: VeldSoort; keuzes: string; verplicht: boolean; kolom: boolean };

export function EigenVeldenBeheer() {
  const { data: d } = useData();
  const [f, setF] = useState<Form | null>(null);
  const volgorde = useActie((ids: string[]) => api.post('/api/eigen-velden/volgorde', { ids }));
  if (!d) return <Laden />;
  const alle = d.eigenVelden ?? [];
  const schuif = (lijst: EigenVeld[], i: number, r: -1 | 1) => {
    const ids = lijst.map((x) => x.id);
    [ids[i], ids[i + r]] = [ids[i + r], ids[i]];
    volgorde.mutate(ids);
  };
  return (
    <div className="pagina kolom" style={{ gap: 16, maxWidth: 1000 }}>
      <div className="paneel">
        <h1>Eigen velden</h1>
        <p className="klein mute" style={{ marginTop: 0 }}>
          Extra velden bij projecten, werkpakketten en taken, bijvoorbeeld een kostenplaats, contractnummer of een eigen fase. Een keuzelijst kan dienen als eigen status. Velden met <b>kolom</b> staan ook in de lijsten. De openbare API en webhooks geven de waarden op sleutel.
        </p>
      </div>
      {VELD_ENTITEITEN.map((ent) => {
        const lijst = alle.filter((v) => v.entiteit === ent).sort((a, b) => a.volgorde - b.volgorde);
        return (
          <div className="paneel" key={ent}>
            <div className="sectie-kop">
              <h2 className="groei">{VELD_ENTITEIT_LABEL[ent]}</h2>
              <button className="knop" onClick={() => setF({ entiteit: ent, naam: '', soort: 'TEKST', keuzes: '', verplicht: false, kolom: false })}><Icoon naam="plus" maat={16} /> Veld</button>
            </div>
            {lijst.length ? (
              <table className="tabel">
                <thead><tr><th>Naam</th><th>Soort</th><th>Sleutel (API)</th><th>Verplicht</th><th>Kolom</th><th style={{ width: 80 }} /></tr></thead>
                <tbody>
                  {lijst.map((v, i) => (
                    <tr key={v.id} className="klik" onClick={() => setF({ id: v.id, entiteit: ent, naam: v.naam, soort: v.soort as VeldSoort, keuzes: v.keuzes.join('\n'), verplicht: v.verplicht, kolom: v.kolom })}>
                      <td className="vet">{v.naam}</td>
                      <td>{VELD_SOORT_LABEL[v.soort as VeldSoort]}{v.soort === 'KEUZE' ? <span className="klein mute"> · {v.keuzes.join(', ')}</span> : null}</td>
                      <td><code className="klein">{v.sleutel}</code></td>
                      <td>{v.verplicht ? 'ja' : ''}</td>
                      <td>{v.kolom ? 'ja' : ''}</td>
                      <td className="nowrap" onClick={(e) => e.stopPropagation()}>
                        <button className="icoon-knop" disabled={i === 0} onClick={() => schuif(lijst, i, -1)} title="Omhoog"><Icoon naam="chevron-up" maat={16} /></button>
                        <button className="icoon-knop" disabled={i === lijst.length - 1} onClick={() => schuif(lijst, i, 1)} title="Omlaag"><Icoon naam="chevron-down" maat={16} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="klein mute">Nog geen eigen velden.</div>
            )}
          </div>
        );
      })}
      {f && <VeldForm f={f} onSluit={() => setF(null)} />}
    </div>
  );
}

function VeldForm({ f: begin, onSluit }: { f: Form; onSluit: () => void }) {
  const [f, setF] = useState(begin);
  const keuzes = f.keuzes.split('\n').map((x) => x.trim()).filter(Boolean);
  const opslaan = useActie(
    () =>
      f.id
        ? api.put(`/api/eigen-velden/${f.id}`, { naam: f.naam, keuzes, verplicht: f.verplicht, kolom: f.kolom })
        : api.post('/api/eigen-velden', { entiteit: f.entiteit, naam: f.naam, soort: f.soort, keuzes, verplicht: f.verplicht, kolom: f.kolom }),
    { succes: 'Veld opgeslagen', opNa: onSluit },
  );
  const weg = useActie(() => api.del(`/api/eigen-velden/${f.id}`), { succes: 'Veld verwijderd', opNa: onSluit });
  return (
    <Modal
      titel={`${f.id ? 'Veld bewerken' : 'Nieuw veld'} · ${VELD_ENTITEIT_LABEL[f.entiteit]}`}
      onSluit={onSluit}
      voet={
        <>
          {f.id && <VerwijderKnop onBevestig={() => weg.mutate()} />}
          <div className="groei" />
          <button className="knop" onClick={onSluit}>Annuleren</button>
          <button className="knop primair" disabled={opslaan.isPending || !f.naam.trim()} onClick={() => opslaan.mutate()}>Opslaan</button>
        </>
      }
    >
      <div className="grid2">
        <Veld label="Naam *"><input value={f.naam} onChange={(e) => setF({ ...f, naam: e.target.value })} autoFocus /></Veld>
        <Veld label="Soort" hint={f.id ? 'De soort kan niet meer veranderen' : undefined}>
          <select value={f.soort} disabled={!!f.id} onChange={(e) => setF({ ...f, soort: e.target.value as VeldSoort })}>
            {VELD_SOORTEN.map((s) => <option key={s} value={s}>{VELD_SOORT_LABEL[s]}</option>)}
          </select>
        </Veld>
        {f.soort === 'KEUZE' && (
          <Veld label="Keuzes (één per regel) *" className="span2">
            <textarea rows={5} value={f.keuzes} onChange={(e) => setF({ ...f, keuzes: e.target.value })} placeholder={'Intake\nOntwerp\nUitvoering\nNazorg'} />
          </Veld>
        )}
        <label className="check"><input type="checkbox" checked={f.verplicht} onChange={(e) => setF({ ...f, verplicht: e.target.checked })} /> Verplicht</label>
        <label className="check"><input type="checkbox" checked={f.kolom} onChange={(e) => setF({ ...f, kolom: e.target.checked })} /> Tonen als kolom in de lijst</label>
      </div>
      {f.id && <p className="klein mute">Verwijderen haalt het veld uit formulieren en lijsten. Ingevulde waarden verdwijnen bij de volgende wijziging van het record.</p>}
    </Modal>
  );
}
